// src/index.ts
import { useEffect } from "react";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import Unit from "../utils/unit.js";

// ../base/src/chains/ethereum/index.ts
import Emitter from "../utils/emitter.js";
import RPCMethod from "../utils/RPCMethod.js";
import detectProvider from "../utils/detect.js";
var EthereumRPCMethod = class extends RPCMethod {
  sessionKey = "ethereum--isMetaMask";
  detectProvider = () => detectProvider({ injectFlag: "ethereum", defaultWaltFlag: "isMetaMask" });
  requestAccounts = () => this.provider.request({ method: "eth_requestAccounts" });
  getAccounts = () => this.provider.request({ method: "eth_accounts" });
  getChainId = () => this.provider.request({ method: "eth_chainId" });
  getBalance = async () => {
    if (!this.account)
      throw new Error("getBalance error: must connect to wallet");
    try {
      const minUnitBalance = await this.provider.request({
        method: "eth_getBalance",
        params: [this.account, "latest"]
      });
      return minUnitBalance;
    } catch (err) {
      throw err;
    }
  };
  onAccountsChange = (callback) => this.provider.on("accountsChanged", callback);
  onChainIdChange = (callback) => this.provider.on("chainChanged", callback);
  sendTransaction = (params) => this.provider.request({
    method: "eth_sendTransaction",
    params: [params]
  });
  personalSign = (message) => this.provider.request({
    method: "personal_sign",
    params: [message, this.account]
  });
  typedSign = (typedData) => this.provider.request({
    method: "eth_signTypedData_v4",
    params: [this.account, JSON.stringify(typedData)]
  });
  addChain = (param) => this.provider.request({
    method: "wallet_addEthereumChain",
    params: [param]
  });
  switchChain = (chainId) => this.provider.request({
    method: "wallet_switchEthereumChain",
    params: [{ chainId }]
  });
  watchAsset = (param) => this.provider.request({
    method: "wallet_watchAsset",
    params: param
  });
  isHostedByFluent = () => this.provider === window.conflux;
  requestPermissions = (params) => this.provider.request({
    method: "wallet_requestPermissions",
    params: [params]
  });
  requestCrossNetworkPermission = () => {
    if (this.isHostedByFluent()) {
      return this.provider.request({
        method: "wallet_requestPermissions",
        params: [{ wallet_accounts: {}, wallet_crossNetworkTypeGetConfluxBase32Address: {}, wallet_crossNetworkTypeGetEthereumHexAddress: {} }]
      });
    }
    return Promise.reject();
  };
};
var ethereum_default = new Emitter(new EthereumRPCMethod());

// src/index.ts
import { subStateChange, selectors } from "../utils/helpers.js";
var store = create(
  subscribeWithSelector(() => ({
    status: "in-detecting",
    accounts: void 0,
    chainId: void 0,
    balance: void 0
  }))
);
subStateChange(ethereum_default, store);
var useStatus = () => store(selectors.status);
var useChainId = () => store(selectors.chainId);
var useAccount = () => store(selectors.account);
var referenceCount = 0;
var useBalance = () => {
  useEffect(() => {
    if (++referenceCount === 1) {
      ethereum_default.startTrackBalance();
    }
    return () => {
      if (--referenceCount === 0) {
        ethereum_default.stopTrackBalance();
      }
    };
  }, []);
  return store(selectors.balance);
};
var startTrackBalance = ethereum_default.startTrackBalance;
var stopTrackBalance = ethereum_default.stopTrackBalance;
var trackBalanceChangeOnce = ethereum_default.trackBalanceChangeOnce;
var completeDetect = ethereum_default.completeDetect;
var connect = ethereum_default.connect;
var sendTransaction = ethereum_default.sendTransaction;
var personalSign = ethereum_default.personalSign;
var typedSign = ethereum_default.typedSign;
var addChain = ethereum_default.addChain;
var switchChain = ethereum_default.switchChain;
var watchAsset = ethereum_default.watchAsset;
var requestPermissions = ethereum_default.requestPermissions;
var requestCrossNetworkPermission = ethereum_default.requestCrossNetworkPermission;
var setCrossNetworkChain = ethereum_default.setCrossNetworkChain;
var provider = ethereum_default.provider;
ethereum_default.completeDetect().then(() => provider = ethereum_default.provider);
export {
  Unit,
  addChain,
  completeDetect,
  connect,
  personalSign,
  provider,
  requestCrossNetworkPermission,
  requestPermissions,
  sendTransaction,
  setCrossNetworkChain,
  startTrackBalance,
  stopTrackBalance,
  store,
  switchChain,
  trackBalanceChangeOnce,
  typedSign,
  useAccount,
  useBalance,
  useChainId,
  useStatus,
  watchAsset
};
